// Maps between the shared SchedularPanel's form data and the BYOD schedule
// API request/response shapes. The panel produces a rich SchedularPanelFormData
// (product / dashboard / path / filters / taskType) that the BYOD contract
// doesn't use — only report metadata, schedule, and time window cross over.

import type {
  SchedularPanelFormData,
  SchedularPanelTimePeriod,
} from 'src/types/analytics-genesis';

import type {
  ByodActiveSchedule,
  ByodScheduleConfig,
  ByodScheduleCreateRequest,
  ByodScheduleUpdateRequest,
} from './schedule.types';

// The five date-range presets the SchedularPanel offers. A schedule created
// elsewhere with an unlisted value (e.g. the API-only 'Last 60 days') is mapped
// to a supported preset so the panel's dropdown shows a valid selection instead
// of a blank.
const PANEL_TIME_WINDOWS: SchedularPanelTimePeriod[] = [
  'Current Year',
  'Last day',
  'Last 7 days',
  'Last 30 days',
  'Last 90 days',
];

// Day counts for the "Last N days" presets, used to map an unsupported value
// to the nearest one.
const DAY_BASED_PRESETS: {label: SchedularPanelTimePeriod; days: number}[] = [
  {label: 'Last day', days: 1},
  {label: 'Last 7 days', days: 7},
  {label: 'Last 30 days', days: 30},
  {label: 'Last 90 days', days: 90},
];

function normalizeTimeWindow(value: string): SchedularPanelTimePeriod {
  if ((PANEL_TIME_WINDOWS as string[]).includes(value)) {
    return value as SchedularPanelTimePeriod;
  }
  // Map an unsupported "Last N days" value to the nearest day-based preset,
  // rounding up to the wider window on a tie so editing never silently narrows
  // the range the user's report was already covering (e.g. 'Last 60 days' → 90).
  const match = value.match(/^Last (\d+) days?$/i);
  if (match) {
    const days = parseInt(match[1], 10);
    return DAY_BASED_PRESETS.reduce((nearest, preset) => {
      const diff = Math.abs(days - preset.days);
      const bestDiff = Math.abs(days - nearest.days);
      return diff < bestDiff ||
        (diff === bestDiff && preset.days > nearest.days)
        ? preset
        : nearest;
    }).label;
  }
  // Non-"Last N days" formats we don't recognize → widest window.
  return 'Last 90 days';
}

// Keep only the schedule field relevant to the chosen frequency, per the API
// contract. BYOD hides the panel's "last day of month" option (the contract
// caps day_of_month at 28 and has no special_day concept), so special_day is
// normally null here; the 'last_day' -> 28 branch below is a defensive fallback
// for any form data that still carries it.
// Range validity (day_of_week 0-6, day_of_month 1-28) is enforced by the panel;
// this mapper only guards against null, not out-of-range values, and the
// backend remains the source of truth for rejection.
export function toByodScheduleConfig(
  schedule: SchedularPanelFormData['schedule'],
): ByodScheduleConfig {
  const base = {frequency: schedule.frequency, time: schedule.time};
  if (schedule.frequency === 'weekly') {
    return {...base, day_of_week: schedule.day_of_week ?? 0};
  }
  if (schedule.frequency === 'monthly') {
    const dayOfMonth =
      schedule.day_of_month ?? (schedule.special_day === 'last_day' ? 28 : 1);
    return {...base, day_of_month: dayOfMonth};
  }
  return base;
}

export function toByodScheduleCreateRequest(
  data: SchedularPanelFormData,
): ByodScheduleCreateRequest {
  return {
    report_name: data.report_payload.report_name,
    emails: data.report_payload.emails,
    subject: data.report_payload.subject,
    time_window: data.time_window,
    agency_tz: data.agency_tz,
    schedule: toByodScheduleConfig(data.schedule),
  };
}

export function toByodScheduleUpdateRequest(
  data: SchedularPanelFormData,
): ByodScheduleUpdateRequest {
  const {agency_tz: _agencyTz, ...rest} = toByodScheduleCreateRequest(data);
  return rest;
}

// Server schedule → panel initialData. `agency_tz` is intentionally omitted
// (the panel reads it from Redux); filters / product / dashboard / path aren't
// part of the BYOD contract and keep their panel-provided defaults.
export function byodScheduleToFormData(
  schedule: ByodActiveSchedule,
): Partial<SchedularPanelFormData> {
  return {
    time_window: normalizeTimeWindow(schedule.time_window),
    schedule: {
      frequency: schedule.schedule.frequency,
      time: schedule.schedule.time,
      day_of_week: schedule.schedule.day_of_week ?? null,
      day_of_month: schedule.schedule.day_of_month ?? null,
      special_day: null,
    },
    report_payload: {
      report_name: schedule.report_name,
      emails: schedule.emails,
      subject: schedule.subject,
    },
  };
}
